import { create } from 'zustand'
import { ChatMessageTelemetry, ChatType, DEFAULT_DATE_MODIFIED_SCORE_WEIGHT, DEFAULT_RELEVANCE_SCORE_WEIGHT, PrefilledIntentType } from '../types';
import { ModelKey, RecordType } from 'bridging';
import { IntentKey } from '../ai/config';
import { UpdateTemplateSchema } from '@/types/templates';
import { useFeatureFlagsStore } from './use-feature-flags-store';
import { UIMessage } from 'ai';

export type RunPromptTemplate = {
  id: string;
  name: string;
  template?: UpdateTemplateSchema['template'],
  disableAutoRun?: boolean
  prompt?: string
  alreadyRan?: boolean
}

type ChatManagerStore = {
  chatId: string | undefined;
  showThinking: boolean;
  showThinkingIfMultipleRetrievals: boolean;
  showThinkingMessageIndex: number | undefined;
  llmThinkingEnabled: boolean;
  chatType: ChatType | null;
  prefilledIntent: PrefilledIntentType;
  selectedContacts: string[];
  excludedRecordTypes: RecordType[];
  enableHybridSearch: boolean;
  enableExhaustiveSearch: boolean;
  exhaustiveSearchEvalSnippets: number;
  exhaustiveSearchFetchAdjacentEvalSnippets: boolean;
  exhaustiveSearchEvalCoT: boolean;
  noCompletion: boolean;
  orderByRelevanceAndDateModifiedWeighted: boolean;
  dateModifiedScoreWeight: number;
  relevanceScoreWeight: number;
  preferDateRangeEnabled: boolean | undefined;
  adminRolledVectorSearchDateParams: { cutoffStartTs: number; cutoffEndTs: number; preferredRangeStartTs: number; preferredRangeEndTs: number; } | undefined;
  fileIds: string[];
  autoSendMessage: string | null;
  runPromptTemplate: RunPromptTemplate | null;
  initialMessages: UIMessage[] | undefined;
  chatTelemetry: Record<string, ChatMessageTelemetry>;
  chatTelemetryLoading: boolean;
  chatTelemetryErrorMessage: string;
  modelKey: ModelKey | null;
  intentModelKey: ModelKey | null;
  intentKey: IntentKey | null;
  lastInput: string;
  lastPrefilledIntent: PrefilledIntentType;
  isLoading: boolean;
  chatIsLoading: boolean;
  messages: UIMessage[];
  hasMessages: boolean;
  messagesLength: number;
  chatPromptTemplateId: string | null;
  reloadPromptTemplateId: string | undefined;
  chatText: string;
  awaitingUserToolResult: boolean;
  isLoadingPlaceholderText: string;
  selectedKnowledgeLibraryIds: string[] | undefined;
  reloadSelectedKnowledgeLibraryIds: string[] | undefined;
  // FOO
  setChatId: (chatId: ChatManagerStore["chatId"]) => void;
  setShowThinking: (showThinking: ChatManagerStore["showThinking"]) => void;
  setShowThinkingIfMultipleRetrievals: (showThinkingIfMultipleRetrievals: ChatManagerStore["showThinkingIfMultipleRetrievals"]) => void;
  setShowThinkingMessageIndex: (showThinkingMessageIndex: ChatManagerStore["showThinkingMessageIndex"]) => void;
  setLlmThinkingEnabled: (llmThinkingEnabled: ChatManagerStore["llmThinkingEnabled"]) => void;
  setChatType: (chatType: ChatManagerStore["chatType"]) => void;
  setPrefilledIntent: (prefilledIntent: ChatManagerStore["prefilledIntent"]) => void;
  setSelectedContacts: (selectedContacts: ChatManagerStore["selectedContacts"]) => void;
  setExcludedRecordTypes: (excludedRecordTypes: ChatManagerStore["excludedRecordTypes"]) => void;
  setEnableHybridSearch: (enableHybridSearch: ChatManagerStore["enableHybridSearch"]) => void;
  setEnableExhaustiveSearch: (enableExhaustiveSearch: ChatManagerStore["enableExhaustiveSearch"]) => void;
  setExhaustiveSearchEvalSnippets: (exhaustiveSearchEvalSnippets: ChatManagerStore["exhaustiveSearchEvalSnippets"]) => void;
  setExhaustiveSearchFetchAdjacentEvalSnippets: (exhaustiveSearchFetchAdjacentEvalSnippets: ChatManagerStore["exhaustiveSearchFetchAdjacentEvalSnippets"]) => void;
  setExhaustiveSearchEvalCoT: (exhaustiveSearchEvalCoT: ChatManagerStore["exhaustiveSearchEvalCoT"]) => void;
  setNoCompletion: (noCompletion: ChatManagerStore["noCompletion"]) => void;
  setOrderByRelevanceAndDateModifiedWeighted: (orderByRelevanceAndDateModifiedWeighted: ChatManagerStore["orderByRelevanceAndDateModifiedWeighted"]) => void;
  setDateModifiedScoreWeight: (dateModifiedScoreWeight: ChatManagerStore["dateModifiedScoreWeight"]) => void;
  setRelevanceScoreWeight: (relevanceScoreWeight: ChatManagerStore["relevanceScoreWeight"]) => void;
  setPreferDateRangeEnabled: (preferDateRangeEnabled: ChatManagerStore["preferDateRangeEnabled"]) => void;
  setAdminRolledVectorSearchDateParams: (adminRolledVectorSearchDateParams: ChatManagerStore["adminRolledVectorSearchDateParams"]) => void;
  setFileIds: (fileIds: ChatManagerStore["fileIds"]) => void;
  setAutoSendMessage: (autoSendMessage: ChatManagerStore["autoSendMessage"]) => void;
  setRunPromptTemplate: (runPromptTemplate: ChatManagerStore["runPromptTemplate"]) => void;
  setInitialMessages: (initialMessages: ChatManagerStore["initialMessages"]) => void;
  setChatTelemetry: (chatTelemetry: ChatManagerStore["chatTelemetry"]) => void;
  setChatTelemetryLoading: (chatTelemetryLoading: ChatManagerStore["chatTelemetryLoading"]) => void;
  setChatTelemetryErrorMessage: (chatTelemetryErrorMessage: ChatManagerStore["chatTelemetryErrorMessage"]) => void;
  setModelKey: (modelKey: ChatManagerStore["modelKey"]) => void;
  setIntentModelKey: (intentModelKey: ChatManagerStore["intentModelKey"]) => void;
  setIntentKey: (intentKey: ChatManagerStore["intentKey"]) => void;
  setLastInput: (lastInput: ChatManagerStore["lastInput"]) => void;
  setLastPrefilledIntent: (lastPrefilledIntent: ChatManagerStore["lastPrefilledIntent"]) => void;
  setIsLoading: (isLoading: ChatManagerStore["isLoading"]) => void;
  setChatIsLoading: (isLoading: ChatManagerStore["chatIsLoading"]) => void;
  setMessages: (messages: ChatManagerStore["messages"]) => void;
  setChatPromptTemplateId: (chatPromptTemplateId: ChatManagerStore["chatPromptTemplateId"]) => void;
  setReloadPromptTemplateId: (reloadPromptTemplateId: ChatManagerStore["reloadPromptTemplateId"]) => void;
  setChatText: (value: ChatManagerStore["chatText"]) => void;
  setAwaitingUserToolResult: (awaitingUserToolResult: ChatManagerStore["awaitingUserToolResult"]) => void;
  setSelectedKnowledgeLibraryIds: (ids: string[] | undefined) => void
  setReloadSelectedKnowledgeLibraryIds: (ids: string[] | undefined) => void
  initSelectedKnowledgeLibraryIds: (ids: string[] | undefined) => void
  // BAR
}

// INPUT

export const useChatManagerStore = create<ChatManagerStore>((set, get) => ({
  chatId: undefined,
  showThinking: false,
  showThinkingIfMultipleRetrievals: false,
  showThinkingMessageIndex: undefined,
  llmThinkingEnabled: false,
  chatType: null,
  prefilledIntent: '',
  selectedContacts: [],
  excludedRecordTypes: [],
  enableHybridSearch: false,
  enableExhaustiveSearch: false,
  exhaustiveSearchEvalSnippets: 3,
  exhaustiveSearchFetchAdjacentEvalSnippets: true,
  exhaustiveSearchEvalCoT: true,
  noCompletion: false,
  orderByRelevanceAndDateModifiedWeighted: false,
  dateModifiedScoreWeight: DEFAULT_DATE_MODIFIED_SCORE_WEIGHT,
  relevanceScoreWeight: DEFAULT_RELEVANCE_SCORE_WEIGHT,
  preferDateRangeEnabled: true,
  adminRolledVectorSearchDateParams: undefined,
  fileIds: [],
  autoSendMessage: null,
  runPromptTemplate: null,
  initialMessages: undefined,
  chatTelemetry: {},
  chatTelemetryLoading: false,
  chatTelemetryErrorMessage: '',
  modelKey: null,
  intentModelKey: null,
  intentKey: null,
  lastInput: '',
  lastPrefilledIntent: '',
  isLoading: true,
  chatIsLoading: false,
  messages: [],
  hasMessages: false,
  messagesLength: 0,
  chatPromptTemplateId: null,
  reloadPromptTemplateId: undefined,
  chatText: '',
  awaitingUserToolResult: false,
  isLoadingPlaceholderText: 'Loading..',
  selectedKnowledgeLibraryIds: undefined,
  reloadSelectedKnowledgeLibraryIds: undefined,
  // BAZ
  setChatId: (chatId) => {
    set({ 
      chatId, 
      selectedContacts: [],
      awaitingUserToolResult: false,
      isLoadingPlaceholderText: 'Loading..'
    });
  },
  setShowThinking: (showThinking) => {
    set({ showThinking })
    if (!showThinking) {
      set({
        showThinkingIfMultipleRetrievals: false,
        showThinkingMessageIndex: undefined,
      })
    }
  },
  setShowThinkingIfMultipleRetrievals: (showThinkingIfMultipleRetrievals) => set({ showThinkingIfMultipleRetrievals }),
  setShowThinkingMessageIndex: (showThinkingMessageIndex) => set({ showThinkingMessageIndex }),
  setLlmThinkingEnabled: (llmThinkingEnabled) => set({ llmThinkingEnabled }),
  setChatType: (chatType) => set({ chatType }),
  setPrefilledIntent: (prefilledIntent) => set({ prefilledIntent }),
  setSelectedContacts: (selectedContacts) => set({ selectedContacts }),
  setExcludedRecordTypes: (excludedRecordTypes) => set({ excludedRecordTypes }),
  setEnableHybridSearch: (enableHybridSearch) => set({ enableHybridSearch }),
  setEnableExhaustiveSearch: (enableExhaustiveSearch) => set({ enableExhaustiveSearch }),
  setExhaustiveSearchEvalSnippets: (exhaustiveSearchEvalSnippets) => set({ exhaustiveSearchEvalSnippets }),
  setExhaustiveSearchFetchAdjacentEvalSnippets: (exhaustiveSearchFetchAdjacentEvalSnippets) => set({ exhaustiveSearchFetchAdjacentEvalSnippets }),
  setExhaustiveSearchEvalCoT: (exhaustiveSearchEvalCoT) => set({ exhaustiveSearchEvalCoT }),
  setNoCompletion: (noCompletion) => set({ noCompletion }),
  setOrderByRelevanceAndDateModifiedWeighted: (orderByRelevanceAndDateModifiedWeighted) => set({ orderByRelevanceAndDateModifiedWeighted }),
  setDateModifiedScoreWeight: (dateModifiedScoreWeight) => set({ dateModifiedScoreWeight }),
  setRelevanceScoreWeight: (relevanceScoreWeight) => set({ relevanceScoreWeight }),
  setPreferDateRangeEnabled: (preferDateRangeEnabled) => set({ preferDateRangeEnabled }),
  setAdminRolledVectorSearchDateParams: (adminRolledVectorSearchDateParams) => set({ adminRolledVectorSearchDateParams }),
  setFileIds: (fileIds) => set({ fileIds }),
  setAutoSendMessage: (autoSendMessage) => set({ autoSendMessage }),
  setRunPromptTemplate: (runPromptTemplate) => set({ runPromptTemplate }),
  setInitialMessages: (initialMessages) => set({ initialMessages }),
  setChatTelemetry: (chatTelemetry) => set({ chatTelemetry }),
  setChatTelemetryLoading: (chatTelemetryLoading) => set({ chatTelemetryLoading }),
  setChatTelemetryErrorMessage: (chatTelemetryErrorMessage) => set({ chatTelemetryErrorMessage }),
  setModelKey: (modelKey) => set({ modelKey }),
  setIntentModelKey: (intentModelKey) => set({ intentModelKey }),
  setIntentKey: (intentKey) => set({ intentKey }),
  setLastInput: (lastInput) => set({ lastInput }),
  setLastPrefilledIntent: (lastPrefilledIntent) => set({ lastPrefilledIntent }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setChatIsLoading: (chatIsLoading) => set({ chatIsLoading }),
  setMessages: (messages) => {
    if (get().messages !== messages) {
      set({ messages });
    }
    const messagesLength = messages.length;
    if (get().messagesLength !== messagesLength) {
      set({ messagesLength });
    }
    const hasMessages = !!messages.length;
    if (get().hasMessages !== hasMessages) {
      set({ hasMessages });
    }
  },
  setChatPromptTemplateId: (chatPromptTemplateId) => set({ chatPromptTemplateId }),
  setReloadPromptTemplateId: (reloadPromptTemplateId) => set({ reloadPromptTemplateId }),
  setChatText: (chatText) => set({ chatText }),
  setAwaitingUserToolResult: (awaitingUserToolResult) => set({ 
    awaitingUserToolResult,
    isLoadingPlaceholderText: awaitingUserToolResult ? 'Please respond to form in chat above..' : 'Loading..'
  }),
  setSelectedKnowledgeLibraryIds: (selectedKnowledgeLibraryIds) => {
    set({ selectedKnowledgeLibraryIds })
  },
  setReloadSelectedKnowledgeLibraryIds: (reloadSelectedKnowledgeLibraryIds) => {
    set({ reloadSelectedKnowledgeLibraryIds })
  },
  initSelectedKnowledgeLibraryIds: (ids) => {
    set({ 
      selectedKnowledgeLibraryIds: ids,
      reloadSelectedKnowledgeLibraryIds: ids
    })
  },
  //QUX
}));

let currentController: AbortController | null = null;

// Subscribe to feature flags store to update llmThinkingEnabled when archie-llm-thinking changes
const subscribeToArchieLLMThinking = () => {
  const unsubscribe = useFeatureFlagsStore.subscribe(
    (state, prevState) => {
      if (state['archie-llm-thinking'] !== prevState['archie-llm-thinking']) {
        useChatManagerStore.setState({ llmThinkingEnabled: !!state['archie-llm-thinking'] });
      }
    }
  );

  // Return cleanup function
  return () => {
    // Abort any ongoing request
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    // Unsubscribe from the store
    unsubscribe();
  };
}

export const initChatManagerStore = () => {
  const disableArchieLLMThinkingSub = subscribeToArchieLLMThinking();

  return [disableArchieLLMThinkingSub];
}

